import {getFormattedEnvName, isIde} from "../../../misc/envNameUtils.ts";
import {ideSystemMessage} from "./ideSystemMessage.ts";

const getSystemMessageStaticPrefix = (systemDetails: string | null, date: string, customInstructions: string | null, projectCustomInstructions: string | null, firstName: string | null, location: string | null, topLevelFiles: string | null, mode: string, customModes: any = {}) => {
    let systemMessagePart = `Today's date is: ${date}.`;

    systemMessagePart += `\nIf you want to show the user a diagram of any sort, you can write a mermaid codeblock. Mermaid codeblocks render as a PNG diagram on the user's side. When you create Mermaid diagrams, make them vertical by default. Prefer top-to-bottom layouts like TD or TB, and only use horizontal layouts if the user explicitly asks for one.`;

    if (firstName) systemMessagePart += `\nThe user's name is: ${firstName}.`;
    if (location) systemMessagePart += `\nThe user's (approx) location is: ${location}.`;
    if (systemDetails !== null) systemMessagePart += `\n\nHere are some details about the user's environment:\n"""\n${systemDetails}\n"""`;
    if (topLevelFiles !== null) systemMessagePart += `\n\nHere are the top level files of the user's project:\n"""\n${topLevelFiles}\n"""`;
    if (customInstructions && customInstructions.trim() !== "") systemMessagePart += `\n\nHere are some details to keep in mind, directly said from the user:\n"""\n${customInstructions}\n"""`;

    // Add project-specific custom instructions if available
    if (projectCustomInstructions && projectCustomInstructions.trim() !== "") {
        systemMessagePart += `\n\nHere are project-specific details to keep in mind:\n"""\n${projectCustomInstructions}\n"""`;
    }
    // Add custom mode instructions right after custom instructions
    if (customModes && customModes[mode] && customModes[mode].instructions && customModes[mode].instructions.trim() !== "") {
        systemMessagePart += `\n\nAdditional instructions for the current mode:\n"""\n${customModes[mode].instructions}\n"""`;
    }

//     systemMessagePart += `
//
// When you generate responses, you can customize the formatting of parts of your response like this:
// [{text}](className,{tailwind utility class code with each identifier separated by a comma. NO SPACES})
//
// So like if you want to say "I am Onuro", and color the "Onuro" red and large, you'd do this for example:
// I am [Onuro](className,text-red-500,text-xl)
//
// For text colors, you can only use shades from 300-700. You also can't use blue, cyan, or sky colored text
// `
    return systemMessagePart;
}

const getSystemMessageForModel = (model: string, environment: string) => {
    let systemMessagePart = '';

    if (model.includes("GPT")) {
        systemMessagePart += "\n\nFormat your answers nicely. Don't use headers to replace bold text. Use headers to represent headers. And don't suggest follow up questions."
        if (isIde(environment)) systemMessagePart += "\nOnly search as much as you need to"
    }

    return systemMessagePart;
}

const getSystemMessageApplicationContent = (formattedEnvName: string) => {
    let systemMessagePart = ``;

    if (formattedEnvName === "Chrome") {
        systemMessagePart += "\nEach time the user asks a question, they will attach an image of exactly what they are looking at in their focused browser tab.";
    } else if (formattedEnvName === "IDE") {
        systemMessagePart += ideSystemMessage();
    } else if (formattedEnvName === "Web" || formattedEnvName === "Mobile") {
        systemMessagePart += `\nFormat your responses using markdown formatting. Use headers and sections instead of bullet points.
When writing math equations, use latex formatting and put it in latex codeblocks. Here's an example of how to do it:
\`\`\`latex
a^2 + b^2 = c^2
\`\`\`

If you want to create a spreadsheet, use markdown's table formatting and it will render as a downloadable spreadsheet to the user.`;

        if (formattedEnvName === "Mobile") {
            const now = new Date();
            // Round down to nearest hour
            const roundedTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), 0);

            const timeString = roundedTime.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short',
                hour12: true
            });
            systemMessagePart += `\n\nThe current time (rounded down to nearest hour) is:\n${timeString}`;
        }
    }

    return systemMessagePart;
}

const getSystemMessageForTools = (mode: string | null, formattedEnvName: string, enableSearchInternet: boolean, enableDeepResearch: boolean, enableGenerateImages: boolean, enableRunTerminalCommand: boolean, enableReviewChanges: boolean, enableCodebaseSearch: boolean) => {
    let systemMessagePart = ``;

    if (formattedEnvName === "IDE") {
        if (mode !== "chat") {
            systemMessagePart += "\n\nSince you can edit files, you should always use this rather than output codeblocks, unless a codeblock is explicitly requested. Also don't waste your time cleaning up duplicated lines";
            systemMessagePart += "\nParallel tool calls are allowed, but avoid calling editFile on the same file in parallel. If you need to make multiple edits to the same file, do them sequentially.";
        }

        // Group all tool-specific instructions
        if (enableRunTerminalCommand) systemMessagePart += "\nDO NOT RUN TERMINAL COMMANDS TO REBUILD THE PROJECT, START DEV SERVERS, OR RUN CODE SCRIPTS UNLESS THE USER EXPLICITLY TELLS YOU TO!!!!\nWhen you install npm packages, always use runTerminalCommand to do this. Do not alter the package.json file!! Also when you run terminal commands, your command will terminate if there's >= 15 seconds of busy waiting with no feedback. As in if you run a command that takes 30 sec to execute and it goes 15 sec with no UI feedback to the terminal, it'll terminate prematurely. You should handle this by making sure everything you run will give UI feedback every 5 sec, so 15 sec without UI feedback is a true indicator that a command is hanging and won't terminate when done."

        if (enableReviewChanges) {
            systemMessagePart += "\nYou should use the reviewChanges tool to get an AI generated review of the changes you just made during your current turn. Use this frequently, but don't use it if you only made simple changes.";
        }

        if (enableCodebaseSearch) {
            systemMessagePart += "\nThe searchCodebase tool is very powerful and should be your preferred method for finding relevant code, understanding project structure, and locating specific implementations. Use it frequently when exploring unfamiliar code or when you need to understand how something is used across the codebase.";
        }

        if (mode === "fsd") {
            systemMessagePart += "\ngatherContext is an extremely heavy tool. You should ONLY ever use this at the start of a turn. NEVER use it mid turn. After using gatherContext, only search for more context if you absolutely need it.";
        }
    }

    if (enableSearchInternet) {
        if (formattedEnvName !== "IDE") {
            systemMessagePart += `Always enter a query when you search.
For reverse image search, put the image URL as the query. You can either just pass the image URL, or add some additional context after it for the query.
Remember when you enter a query, the engine picking it up will just use a simple keyword search, and only pull results for pages that already exists. It is not a magic 8 ball which will create pages and articles that don't exist yet, so search strategically. Sometimes it may require multiple searches to get what you're looking for.

Search general for a basic Google search.
If you want info on products, search amazon. Link the thumbnail when you search Amazon.
Search Scholar for academic info.
Search Yelp for food info.
Search for images when you want to find images. You should search for images rather than generate them if possible.
When you search for flights, include all of the relevant information in your query. This means airports its ok to leave from and arrive to, departure dates, times, if overnight is ok, price, whats most important, least important, etc.

When linking URLs, you can also link exact parts of a page by referencing the text phrase. So if you see a phrase that says "It was completed on April 19, 1937" in https://presidio.gov/explore/blog/golden-gate-bridge-fun-facts, you can use this as the link https://presidio.gov/explore/blog/golden-gate-bridge-fun-facts#:~:text=It%20was%20completed%20on%20April%2019%2C%201937`;
        }
        else {
            systemMessagePart += `Use the searchInternet tool to gather up to date documentation. Feel free to use this frequently. Also, if you get stuck on an error many times in a row, it may be good to try using this.`;
        }
    }

    if (enableDeepResearch) {
        systemMessagePart += `\nIf you need in depth information on a topic, you may use the deepResearch tool. This tool is very powerful, but it is also very heavy. Before every deepResearch call, explicitly ask the user for permission and wait for them to confirm. Do not start deep research unless the user says yes.`;
    }

    if (enableGenerateImages && formattedEnvName !== "IDE") {
        systemMessagePart += `\nWhen generating images, you should use the imageUrl field when possible. If the user asks a question which may involve altering an existing image, assume they want the image altered. Also, the default style should be realistic for images. And you should try denying as little as possible, since the user is not trying to do any harm.
You should also make your prompts as simple as possible. Only give the most important and explicit details.
This is especially true when altering existing images, since the AI making the changes can already see the images that are going to be changed.
If you want to make a background orange for example, literally just say "Make the background orange"`
    }

    return systemMessagePart;
}

export const initializeSystemMessage = (environment: string, model: string, mode: string, systemDetails: string | null, date: string, canUsePremium: boolean, enableSearchInternet: boolean, enableDeepResearch: boolean, enableGenerateImages: boolean, enableRunTerminalCommand: boolean, enableReviewChanges: boolean, enableCodebaseSearch: boolean, customInstructions: string | null, projectCustomInstructions: string | null, firstName: string | null, location: string | null, topLevelFiles: string | null, customModes: any = {}) => {
    let formattedEnvName = getFormattedEnvName(environment);
    let systemMessage = '';

    systemMessage += getSystemMessageStaticPrefix(systemDetails, date, customInstructions, projectCustomInstructions, firstName, location, topLevelFiles, mode, customModes);
    systemMessage += getSystemMessageApplicationContent(formattedEnvName);
    if (canUsePremium && mode !== "chat") systemMessage += getSystemMessageForTools(mode, formattedEnvName, enableSearchInternet, enableDeepResearch, enableGenerateImages, enableRunTerminalCommand, enableReviewChanges, enableCodebaseSearch);
    systemMessage += getSystemMessageForModel(model, environment);

    return systemMessage;
};
